import { NavigationEnd, NavigationStart, Router } from '@angular/router';
import { WindowService } from './window.service';
import { fromEvent, ReplaySubject, Subject } from 'rxjs';
import { PlatformService } from './platform.service';
import { Injectable, inject, DOCUMENT } from '@angular/core';
import { map, tap, filter, switchMap } from 'rxjs/operators';
import { BreakpointsDetectorService } from './breakpoints-detector.service';
import { isDesktop, isMobile, isTablet } from '../signals/device';
import { DeviceDetectorService } from 'ngx-unificator/services';

export interface WindowSize {
  width: number | any;
  height: number | any;
}

export const IdLinkAsSLugForCms: string[] = ['currency-exchange'];

@Injectable({
  providedIn: 'root',
})
export class GlobalEventsService {
  private _platform = inject(PlatformService);
  private _window = inject(WindowService);
  private _router = inject(Router);
  private _breakpoints = inject(BreakpointsDetectorService);
  private _device = inject(DeviceDetectorService);
  private _document = inject<Document>(DOCUMENT);


  public resize$: ReplaySubject<any> = new ReplaySubject(1);
  public orientationchange$: ReplaySubject<any> = new ReplaySubject(1);
  public scroll$: ReplaySubject<any> = new ReplaySubject(1);
  public beforeinstallprompt$: ReplaySubject<any> = new ReplaySubject(1);
  public routerNavigationStart$: ReplaySubject<any> = new ReplaySubject(1);
  public routerNavigationEnd$: ReplaySubject<any> = new ReplaySubject(1);
  public globalRouterEvent$: ReplaySubject<any> = new ReplaySubject(1);
  public fullscreenChangeEvent$: ReplaySubject<any> = new ReplaySubject(1);
  public handleEnterClick$: Subject<any> = new Subject<any>();
  public isHomePage$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);
  public isLandscape$: ReplaySubject<any> = new ReplaySubject(1);
  public initialLandscape$: ReplaySubject<any> = new ReplaySubject(1);
  public isLeaveBrowserTab$: ReplaySubject<any> = new ReplaySubject(1);
  public isVisibleTab$: ReplaySubject<boolean> = new ReplaySubject(1);
  public isBackButtonClick$: ReplaySubject<boolean> = new ReplaySubject(1);
  public isExchangeRateLinkClick$: ReplaySubject<any> = new ReplaySubject<any>(1);

  private _isLeaveBrowserTab = null;

  public initGlobalEvent() {
    this._runRouterNavigationListener();
    if (this._platform.isBrowser) {
      isMobile.set(this._device.isMobile());
      isDesktop.set(this._device.isDesktop());
      isTablet.set(this._device.isTablet());
      this._runScrollListener();
      this._runResizeListener();
      this._runOrientationChangeListener();
      this._runBeforeInstallPromptListener();
      this._runFullscreenChangeEventListener();
      this._runEnterClick();
      this._runLandscapeChecker();
      this._handlerLeaveBrowserTab();
      this._handlerVisibilityChange();
      this._handlerHashChange();
      this._runClickListener();
    }
  }

  private _handlerHashChange() {
    fromEvent(this._window.nativeWindow, 'popstate').subscribe((event) => {
      this.isBackButtonClick$.next(true);
    });
  }

  /**
   * This function listens for changes in the visibility state of the document and updates a
   * BehaviorSubject accordingly.
   */
  private _handlerVisibilityChange() {
    fromEvent(this._document, 'visibilitychange').subscribe(() => {
      if (this._document.visibilityState === 'visible') {
        this.isVisibleTab$.next(true);
      } else {
        this.isVisibleTab$.next(false);
      }
    });
  }

  /**
   * Run global scroll listener
   */
  private _runScrollListener() {
    fromEvent(this._window.nativeWindow, 'scroll', { passive: true, capture: false })
      .subscribe((e) => this.scroll$.next(e));
  }

  /**
   * Run global resize listener
   */
  private _runResizeListener() {
    this._document.documentElement.style.setProperty('--app-height', `${this._window.nativeWindow.innerHeight}px`);
    fromEvent(this._window.nativeWindow, 'resize', { passive: true }).pipe(
      map(event => <WindowSize>{
        width: (<any>window).innerWidth,
        height: (<any>window).innerHeight,
      }),
      tap(e => {
        this._document.documentElement.style.setProperty('--app-height', `${e.height}px`);
      }),
    ).subscribe((e) => this.resize$.next(e));
  }

  /**
   * Run global orientation change listener
   */
  private _runOrientationChangeListener() {
    fromEvent(this._window.nativeWindow, 'orientationchange', { passive: true })
      .subscribe((e) => this.orientationchange$.next(e));
  }

  /**
   * Run global before install prompt listener
   */
  private _runBeforeInstallPromptListener() {
    fromEvent(this._window.nativeWindow, 'beforeinstallprompt')
      .subscribe((e) => this.beforeinstallprompt$.next(e));
  }

  /**
   * Run global router event listener
   */
  private _runRouterNavigationListener() {
    this._router.events.subscribe((e) => {
      if (e instanceof NavigationStart) {
        this.routerNavigationStart$.next(e);
      }
      if (e instanceof NavigationEnd) {
        this.routerNavigationEnd$.next(e);
        if (e?.url?.split('/').length <= 2) {
          this.isHomePage$.next(true);
        } else {
          this.isHomePage$.next(false);
        }
      }
      this.globalRouterEvent$.next(e);
    });
  }

  /**
   * Run global fullscreenchangeEvent listener
   */
  private _runFullscreenChangeEventListener() {
    fromEvent(this._window.nativeWindow, 'fullscreenchange')
      .subscribe((e) => this.fullscreenChangeEvent$.next(e));
  }

  /**
   * Run global click by Enter key
   * @private
   */
  private _runEnterClick() {
    fromEvent(this._document.documentElement, 'keyup')
      .pipe(
        tap((e: any) => e.stopImmediatePropagation()),
        filter((e: any) => e.keyCode === 13),
        tap((e) => {
          this.handleEnterClick$.next(e);
        }),
      ).subscribe();
  }

  /**
   * Run global landscape checker
   */
  private _runLandscapeChecker() {
    fromEvent(this._window.nativeWindow, 'orientationchange').pipe(
      switchMap(() => this._breakpoints.updatedBreakpoints$),
    ).subscribe((e) => this.isLandscape$.next(('orientation' in window) && !(window.orientation === 0 || window.orientation === 180)));

    this.initialLandscape$.next(('orientation' in window) && !(window.orientation === 0 || window.orientation === 180));
  }

  private _handlerLeaveBrowserTab() {
    if (this._platform.isBrowser) {
      fromEvent(this._window.nativeWindow, 'blur').subscribe((e: any) => {
        if (e.eventPhase && !this._isLeaveBrowserTab) {
          this._isLeaveBrowserTab = true;
          this.isLeaveBrowserTab$.next(true);
        }
      });
      fromEvent(this._window.nativeWindow, 'focus').subscribe((e: any) => {
        if (e.eventPhase && this._isLeaveBrowserTab) {
          this._isLeaveBrowserTab = false;
          this.isLeaveBrowserTab$.next(false);
        }
      });
    }
  }

  private _runClickListener() {
    fromEvent(this._document, 'click').subscribe(e => {
      const target = e?.target as HTMLElement;
      switch (true) {
        case target?.nodeName === 'A' && IdLinkAsSLugForCms.includes(target?.id?.trim()):
          this.isExchangeRateLinkClick$.next(target?.id?.trim());
          break;
      }
    });
  }

}
